import { useEffect } from 'react'
import { useNotifications } from '../contexts/NotificationContext'
import { useSocket } from '../contexts/SocketContext'

// Hook to use notification service with context
export const useNotificationService = () => {
  const { addNotification } = useNotifications()
  const { socket, isConnected } = useSocket()

  useEffect(() => {
    if (!socket || !isConnected) return

    console.log('🔧 Initializing notification service listeners...')

    const handleOrderReceived = (data: any) => {
      console.log('📥 Notification Service: New Order', data)
      addNotification({
        title: 'New Order Received',
        message: `Order #${data.order?.orderNumber || 'Unknown'} - ${data.order?.total || 0} SDG`,
        type: 'success',
        action: {
          label: 'View Order',
          onClick: () => {
            window.location.href = `/restaurant/${data.order?.restaurant || 'unknown'}/orders` // simplified nav
          }
        }
      }, true)
    }

    const handleOrderUpdated = (data: any) => {
      console.log('📥 Notification Service: Order Updated', data)
      addNotification({
        title: 'Order Updated',
        message: `Order #${data.orderId.substring(0, 8)}... status updated to ${data.newStatus}`,
        type: 'info',
      }, true)
    }

    const handleTableUpdated = (data: any) => {
      console.log('📥 Notification Service: Table Updated', data)
      addNotification({
        title: 'Table Status Changed',
        message: `Table ${data.tableId} is now ${data.newStatus}`,
        type: 'info',
      }, true)
    }

    const handleFoodItemUpdated = (data: any) => {
      console.log('📥 Notification Service: Food Item Updated', data)
      addNotification({
        title: 'Food Item Updated',
        message: `Item ${data.itemId} is now ${data.isAvailable ? 'Available' : 'Unavailable'}`,
        type: 'warning',
      }, true)
    }

    // Dashboard updates might be generic, so we check type
    const handleDashboardUpdated = (data: any) => {
      console.log('📥 Notification Service: Dashboard Updated', data)
      // Only notify for specific dashboard events if needed to avoid noise
      if (data.updateType === 'alert') {
        addNotification({
          title: 'Alert',
          message: JSON.stringify(data.data),
          type: 'error'
        }, true)
      }
    }

    // Attach listeners
    socket.on('order_received', handleOrderReceived)
    socket.on('order_updated', handleOrderUpdated)
    socket.on('table_updated', handleTableUpdated)
    socket.on('food_item_updated', handleFoodItemUpdated)
    socket.on('dashboard_updated', handleDashboardUpdated)

    return () => {
      console.log('🧹 Cleaning up notification service listeners...')
      socket.off('order_received', handleOrderReceived)
      socket.off('order_updated', handleOrderUpdated)
      socket.off('table_updated', handleTableUpdated)
      socket.off('food_item_updated', handleFoodItemUpdated)
      socket.off('dashboard_updated', handleDashboardUpdated)
    }
  }, [socket, isConnected, addNotification])

  const send = (eventName: string, data: any) => {
    if (socket && isConnected) {
      socket.emit(eventName, data)
      console.log('📤 Sent Socket.IO event:', eventName, data)
    } else {
      console.warn('⚠️ Socket.IO not connected, cannot send event:', eventName, data)
    }
  }

  return {
    isConnected,
    socket,
    send,
    isWebSocketConnected: () => isConnected
  }
}

export default useNotificationService
